import type {
    LogicActionNode,
    LogicConditionNode,
    LogicGroupNode,
    LogicNode,
    LogicReferenceNode,
    LogicScheduleNode,
    LogicSequenceNode,
    TradeLogicJsonValue
} from '../model';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto
} from '../registry';

export interface NodeSummaryFormatContext {
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
}

export const formatLogicNodeSummary = (node: LogicNode, context: NodeSummaryFormatContext): string => {
    if (node.label && node.label.trim().length > 0 && !isAutoGeneratedLabel(node.label)) {
        return node.label;
    }

    switch (node.kind) {
        case 'group':
            return formatGroupSummary(node);
        case 'sequence':
            return formatSequenceSummary(node);
        case 'condition':
            return formatConditionSummary(node, context);
        case 'action':
            return formatActionSummary(node, context);
        case 'schedule':
            return formatScheduleSummary(node);
        case 'reference':
            return formatReferenceSummary(node);

        default:
            return JSON.stringify(node);
    }
};

const formatGroupSummary = (node: LogicGroupNode): string => {
    switch (node.operator) {
        case 'all':
            return 'All of the following';
        case 'any':
            return 'Any of the following';
        case 'not':
            return 'None of the following';
        case 'firstMatch':
            return 'First matching condition';
        case 'threshold':
            return node.threshold !== null && node.threshold !== undefined
                ? `At least ${node.threshold} of the following`
                : 'Threshold of the following';
        case 'weighted':
            return node.weightThreshold !== null && node.weightThreshold !== undefined
                ? `Weighted ≥ ${node.weightThreshold} of the following`
                : 'Weighted group';

        default:
            return 'Group';
    }
};

const formatSequenceSummary = (node: LogicSequenceNode): string => {
    const window = formatSequenceWindow(node);
    const mode = node.mode === 'firstMatch' ? 'First match in sequence' : node.mode === 'allActions' ? 'All actions in order' : 'In order';

    if (window) return `${mode}, ${window}`;
    return mode;
};

const formatSequenceWindow = (node: LogicSequenceNode): string | null => {
    const parts = node.children.flatMap((child, index) => {
        const timing = node.stepTiming[child.id];
        if (!timing?.within) return [];
        const range = timing.within;
        const unit = range.unit === 'sessions' ? 'trading days' : range.unit;
        return [`step ${index + 1}: ${range.minimum ?? 0}–${range.value} ${unit}${range.duration ? ` (${range.duration})` : ''}`];
    });
    return parts.length ? parts.join(', ') : null;
};

const formatConditionSummary = (node: LogicConditionNode, context: NodeSummaryFormatContext): string => {
    if (node.templateKey) {
        const template = context.conditionTemplatesByKey[node.templateKey];
        const label = template?.displayLabel ?? template?.label ?? node.templateKey;
        const params = node.parameters ? formatParameters(node.parameters) : null;
        const operator = formatOperator(node.operator, node.value);
        const operatorClause = operator ? ` ${operator}` : '';
        return params ? `${label} (${params})${operatorClause}` : `${label}${operatorClause}`;
    }

    if (node.featureRef) {
        const feature = context.featuresByKey[node.featureRef.featureKey];
        const label = feature?.displayLabel ?? feature?.label ?? node.featureRef.featureKey;
        const selectorSummary = formatFeatureSelectors(node.featureRef.selectors, feature);
        const operatorClause = formatOperator(node.operator, node.value);
        const segments = [label];
        if (selectorSummary) segments[0] = `${segments[0]} (${selectorSummary})`;
        if (operatorClause) segments.push(operatorClause);
        return segments.join(' ');
    }

    return 'Condition (unconfigured)';
};

const formatActionSummary = (node: LogicActionNode, context: NodeSummaryFormatContext): string => {
    if (node.templateKey) {
        const template = context.actionTemplatesByKey[node.templateKey];
        const label = template?.displayLabel ?? template?.label ?? node.templateKey;
        const params = formatParameters(node.parameters);
        return params ? `${label} (${params})` : label;
    }

    const params = formatParameters(node.parameters);
    return params ? `${formatActionKindLabel(node.actionKind)} (${params})` : formatActionKindLabel(node.actionKind);
};

const formatActionKindLabel = (actionKind: string): string => {
    switch (actionKind) {
        case 'enter':
            return 'Enter trade';
        case 'exit':
            return 'Exit trade';
        case 'partialExit':
            return 'Partial exit';
        case 'moveStop':
            return 'Move stop';
        case 'moveTarget':
            return 'Move target';
        case 'armOrder':
            return 'Arm order';
        case 'cancelOrder':
            return 'Cancel order';
        case 'adjustOrder':
            return 'Adjust order';
        case 'hold':
            return 'Hold';
        case 'noAction':
            return 'No action';
        default:
            return actionKind;
    }
};

const formatScheduleSummary = (node: LogicScheduleNode): string => {
    const params = formatParameters(node.parameters);
    const label = formatScheduleKindLabel(node.scheduleKind);
    return params ? `${label}: ${params}` : label;
};

const formatScheduleKindLabel = (kind: LogicScheduleNode['scheduleKind']): string => {
    switch (kind) {
        case 'marketSession':
            return 'During market session';
        case 'timeOfDay':
            return 'Time of day';
        case 'dayOfWeek':
            return 'Day of week';
        case 'entryAge':
            return 'Bars since entry';
        case 'optionExpiry':
            return 'Before option expiry';
        case 'custom':
            return 'Custom schedule';

        default:
            return 'Schedule';
    }
};

const formatReferenceSummary = (node: LogicReferenceNode): string => {
    const idText = String(node.referenceId);
    const versionText = node.version ? ` v${node.version}` : '';

    switch (node.referenceKind) {
        case 'scorecard':
            return `Scorecard #${idText}`;
        case 'screener':
            return `Screener #${idText}`;
        case 'definition':
            return `Definition #${idText}${versionText}`;
        case 'version':
            return `Version #${idText}`;
        case 'candidate':
            return `Candidate #${idText}`;
        case 'block':
            return `Block #${idText}`;

        default:
            return `Reference #${idText}`;
    }
};

const formatOperator = (operator: LogicConditionNode['operator'], value: TradeLogicJsonValue | undefined): string | null => {
    if (!operator) return null;

    if (operator === 'empty') return 'is empty';
    if (operator === 'not_empty') return 'is not empty';

    if (value === null || value === undefined) return operator;

    return `${operator} ${formatJsonValue(value)}`;
};

const formatFeatureSelectors = (
    selectors: Record<string, TradeLogicJsonValue> | null | undefined,
    feature: TradeLogicRegistryFeatureDto | undefined
): string | null => {
    if (!selectors) return null;

    const parts: string[] = [];

    for (const [key, rawValue] of Object.entries(selectors)) {
        if (rawValue === null || rawValue === undefined || rawValue === '') continue;

        const fieldDefault = feature?.selectorFields.find((field) => field.key === key)?.defaultValue;
        if (fieldDefault !== undefined && fieldDefault !== null && jsonEquals(fieldDefault, rawValue)) continue;

        parts.push(`${key}=${formatJsonValue(rawValue)}`);
    }

    return parts.length > 0 ? parts.join(', ') : null;
};

const formatParameters = (parameters: Record<string, TradeLogicJsonValue> | null | undefined): string | null => {
    if (!parameters) return null;

    const parts: string[] = [];

    for (const [key, value] of Object.entries(parameters)) {
        if (value === null || value === undefined || value === '') continue;
        parts.push(`${key}=${formatJsonValue(value)}`);
    }

    return parts.length > 0 ? parts.join(', ') : null;
};

const formatJsonValue = (value: TradeLogicJsonValue): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return Number.isInteger(value) ? value.toString() : value.toFixed(4).replace(/\.?0+$/, '');
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (value === null) return 'null';
    return JSON.stringify(value);
};

const jsonEquals = (left: TradeLogicJsonValue, right: TradeLogicJsonValue): boolean => {
    if (left === right) return true;
    if (typeof left !== typeof right) return false;
    if (left === null || right === null) return left === right;
    return JSON.stringify(left) === JSON.stringify(right);
};

const isAutoGeneratedLabel = (label: string): boolean => label.startsWith('node-') || /^auto-/i.test(label);
